<template>
  <AwDatepicker
    ref="datepicker"
    :model-value="dateValue"
    :min-date="minDate"
    :max-date="maxDate"
    :disabled="disabled"
    class="w-full"
    :formats="{ preview: previewFormat }"
    :floating="{ placement: position }"
    @update:model-value="emit('update:dateValue', $event)"
  >
    <template #trigger>
      <slot name="trigger">
        <AwTextField
          :model-value="displayedValue"
          variant="outline"
          :size="size"
          :leading-icon="leadingIcon"
          :color="isError ? 'critical' : 'default'"
          class="w-full"
          :placeholder="placeholder ?? $t('common.datepicker.placeholder')"
          :disabled="disabled"
        />
      </slot>
    </template>

    <template #action-buttons="{ value }">
      <div class="flex gap-2">
        <AwButton
          size="sm"
          variant="outline"
          color="secondary"
          :label="$t('common.actions.cancel')"
          @click="cancel()"
        />
        <AwButton
          size="sm"
          variant="solid"
          :label="$t('common.actions.select')"
          @click="selectDate(value as DatepickerValue)"
        />
      </div>
    </template>
  </AwDatepicker>
</template>

<script setup lang="ts">
import { dateFormat } from "@asap/shared/utils/date";

export type DatepickerValue =
  string | Date | Array<string> | Record<"day" | "month" | "year", number> | number | undefined;

type Props = {
  dateValue: DatepickerValue;
  minDate?: Date | string;
  maxDate?: Date | string;
  position?: "top" | "bottom";
  placeholder?: string;
  previewFormat?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  leadingIcon?: string;
  isError?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  position: "bottom",
  minDate: undefined,
  maxDate: undefined,
  placeholder: undefined,
  previewFormat: "dd/MM/yyyy",
  disabled: false,
  size: "sm",
  leadingIcon: undefined,
  isError: false,
});

type Emits = {
  "update:dateValue": [value: DatepickerValue];
};
const emit = defineEmits<Emits>();

defineSlots<{
  trigger?: () => unknown;
}>();

const datepicker = useTemplateRef("datepicker");

const selectDate = (date: DatepickerValue) => {
  emit("update:dateValue", date);
  datepicker.value?.closeMenu();
};
const cancel = () => {
  emit("update:dateValue", undefined);
  datepicker.value?.closeMenu();
};

const displayedValue = computed(() => {
  const isString = typeof props.dateValue === "string";
  const isNumber = typeof props.dateValue === "number";
  const isDate = props.dateValue instanceof Date;

  if (isString || isNumber || isDate) {
    return dateFormat(props.dateValue, { options: { dateStyle: "short" } });
  }
  return props.dateValue;
});
</script>

<style scoped lang=""></style>
